import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfig, SCAN_EDITING } from '../contexts/ConfigContext';
import { flattenScansForLightbox } from '../utils/rollUtils';
import {
  IDENTITY_ADJUST,
  buildRecipe,
  centredCropForAspect,
  clampCrop,
  isNoopRecipe,
  toCssFilter,
  validateRecipe,
  type AdjustSpec,
  type BorderSpec,
  type CropSpec,
  type Rotation,
} from '../utils/editRecipe';
import type { ScansResponse } from '../types/roll';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './EditorPage.module.css';

async function fetchScans(orderId: string): Promise<ScansResponse> {
  const res = await fetch(`/api/v1/orders/${orderId}/scans`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Failed to fetch scans (${res.status})`);
  }
  return res.json();
}

/** Crop aspect presets (visual width : height); null = free-form. */
const CROP_PRESETS: { label: string; aspect: number | null }[] = [
  { label: 'Free', aspect: null },
  { label: '1:1', aspect: 1 },
  { label: '3:2', aspect: 3 / 2 },
  { label: '2:3', aspect: 2 / 3 },
  { label: '4:5', aspect: 4 / 5 },
  { label: '16:9', aspect: 16 / 9 },
];

/** Border frame presets. Kept in the same set and order as CROP_PRESETS
 *  (minus Free) for consistency; the backend accepts any ratio within 1:5–5:1. */
const BORDER_PRESETS: { label: string; aspect_w: number; aspect_h: number }[] = [
  { label: '1:1', aspect_w: 1, aspect_h: 1 },
  { label: '3:2', aspect_w: 3, aspect_h: 2 },
  { label: '2:3', aspect_w: 2, aspect_h: 3 },
  { label: '4:5', aspect_w: 4, aspect_h: 5 },
  { label: '16:9', aspect_w: 16, aspect_h: 9 },
];

interface SliderConfig {
  key: keyof AdjustSpec;
  label: string;
  min: number;
  max: number;
  step: number;
}

const SLIDERS: SliderConfig[] = [
  { key: 'brightness', label: 'Exposure', min: 0, max: 2, step: 0.01 },
  { key: 'contrast', label: 'Contrast', min: 0, max: 2, step: 0.01 },
  { key: 'saturation', label: 'Saturation', min: 0, max: 2, step: 0.01 },
  { key: 'hue', label: 'Hue', min: -180, max: 180, step: 1 },
  { key: 'grayscale', label: 'Black & white', min: 0, max: 1, step: 0.01 },
  { key: 'sepia', label: 'Sepia', min: 0, max: 1, step: 0.01 },
];

/** The eight resize handles: four corners and four edge midpoints. */
type CropHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const CROP_HANDLES: CropHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

// Explicit handle → CSS-module class map. Vite's CSS modules use
// `camelCaseOnly`, so a dynamic `styles['handle_nw']` lookup resolves to
// undefined and the handle loses its positioning — hence the explicit map.
const HANDLE_CLASS: Record<CropHandle, string> = {
  nw: styles.handleNw,
  n: styles.handleN,
  ne: styles.handleNe,
  e: styles.handleE,
  se: styles.handleSe,
  s: styles.handleS,
  sw: styles.handleSw,
  w: styles.handleW,
};

type DragMode =
  | { kind: 'move'; startX: number; startY: number; origin: CropSpec }
  | { kind: 'resize'; handle: CropHandle; startX: number; startY: number; origin: CropSpec };

export function EditorPage() {
  const { orderId, scanId } = useParams<{ orderId: string; scanId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { config, loading: configLoading } = useConfig();
  const editingEnabled = config.features[SCAN_EDITING] === true;

  // Edit state
  const [rotate, setRotate] = useState<Rotation>(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [crop, setCrop] = useState<CropSpec | null>(null);
  const [cropAspect, setCropAspect] = useState<number | null>(null);
  const [border, setBorder] = useState<BorderSpec | null>(null);
  const [adjust, setAdjust] = useState<AdjustSpec>(IDENTITY_ADJUST);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragMode | null>(null);

  // Reuse the gallery's cached scans payload (same key) to find the source scan.
  const { data, isLoading, isError } = useQuery<ScansResponse>({
    queryKey: ['scans', orderId, false],
    queryFn: () => fetchScans(orderId!),
    enabled: !!orderId,
  });

  const scan = useMemo(() => {
    if (!data?.rolls) return undefined;
    return flattenScansForLightbox(data.rolls).find((s) => s.scan_id === scanId);
  }, [data?.rolls, scanId]);

  usePageTitle(scan ? `Edit — ${scan.original_filename}` : 'Edit');

  // The feature flag is enforced server-side; this redirect just keeps the
  // route unreachable in normal navigation. Wait for config to load so a slow
  // config fetch doesn't bounce an allowlisted customer.
  useEffect(() => {
    if (!configLoading && !editingEnabled) {
      navigate(`/orders/${orderId}`, { replace: true });
    }
  }, [configLoading, editingEnabled, navigate, orderId]);

  const galleryPath = `/orders/${orderId}`;

  // Preview transform: rotate then flip, matching the render pipeline. The
  // rotated image is drawn onto its own canvas-sized box, so crop coordinates
  // are relative to what's on screen.
  const isQuarterTurn = rotate === 90 || rotate === 270;
  const transform = [
    `rotate(${rotate}deg)`,
    flipH ? 'scaleX(-1)' : '',
    flipV ? 'scaleY(-1)' : '',
  ]
    .filter(Boolean)
    .join(' ');
  // CSS rotates AFTER flips when composed right-to-left; we want rotate first,
  // then flips in the rotated frame — which is exactly left-to-right order in
  // a CSS transform list, matching the string above.

  const cssFilter = toCssFilter(adjust);

  // ─── Crop interactions ─────────────────────────────────────────────────────

  const pointerToUnit = useCallback((clientX: number, clientY: number) => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };
    const rect = stage.getBoundingClientRect();
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  }, []);

  const startDrag = useCallback(
    (event: ReactPointerEvent, drag: DragMode['kind'], handle?: CropHandle) => {
      if (!crop) return;
      event.preventDefault();
      event.stopPropagation();
      // Capture on the stage (which owns the move/up handlers) so a drag keeps
      // tracking even when the pointer leaves the small handle or the image.
      stageRef.current?.setPointerCapture?.(event.pointerId);
      const { x, y } = pointerToUnit(event.clientX, event.clientY);
      dragRef.current =
        drag === 'move'
          ? { kind: 'move', startX: x, startY: y, origin: crop }
          : { kind: 'resize', handle: handle!, startX: x, startY: y, origin: crop };
    },
    [crop, pointerToUnit],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      event.preventDefault();
      const { x, y } = pointerToUnit(event.clientX, event.clientY);
      const dx = x - drag.startX;
      const dy = y - drag.startY;
      const o = drag.origin;

      if (drag.kind === 'move') {
        setCrop(clampCrop({ ...o, x: o.x + dx, y: o.y + dy }));
        return;
      }

      // Each handle moves only the edges it touches: corners move two, edges
      // move one. The opposite edge(s) stay put.
      const h = drag.handle;
      const movesLeft = h === 'nw' || h === 'w' || h === 'sw';
      const movesRight = h === 'ne' || h === 'e' || h === 'se';
      const movesTop = h === 'nw' || h === 'n' || h === 'ne';
      const movesBottom = h === 'sw' || h === 's' || h === 'se';

      let left = o.x;
      let top = o.y;
      let right = o.x + o.width;
      let bottom = o.y + o.height;
      if (movesLeft) left += dx;
      if (movesRight) right += dx;
      if (movesTop) top += dy;
      if (movesBottom) bottom += dy;

      if (cropAspect !== null) {
        // Keep the fixed edge/corner anchored and derive the free dimension so
        // the box holds its aspect. Unit space is anisotropic, so convert via
        // the stage's aspect ratio.
        const stage = stageRef.current;
        const stageAspect = stage
          ? stage.getBoundingClientRect().width / stage.getBoundingClientRect().height
          : 1;
        const unitRatio = cropAspect / stageAspect; // width / height in unit space
        const horizontal = movesLeft || movesRight;
        const vertical = movesTop || movesBottom;

        if (horizontal && !vertical) {
          // Side handle (e/w): width drives, grow height about the centre.
          const width = Math.max(0.02, Math.abs(right - left));
          const height = width / unitRatio;
          const cy = o.y + o.height / 2;
          top = cy - height / 2;
          bottom = cy + height / 2;
        } else if (vertical && !horizontal) {
          // Side handle (n/s): height drives, grow width about the centre.
          const height = Math.max(0.02, Math.abs(bottom - top));
          const width = height * unitRatio;
          const cx = o.x + o.width / 2;
          left = cx - width / 2;
          right = cx + width / 2;
        } else {
          // Corner: width drives, height follows, anchored to the fixed corner.
          const width = Math.max(0.02, Math.abs(right - left));
          const height = width / unitRatio;
          if (movesTop) top = bottom - height;
          else bottom = top + height;
        }
      }

      setCrop(
        clampCrop({
          x: Math.min(left, right),
          y: Math.min(top, bottom),
          width: Math.max(0.02, Math.abs(right - left)),
          height: Math.max(0.02, Math.abs(bottom - top)),
        }),
      );
    },
    [cropAspect, pointerToUnit],
  );

  const endDrag = useCallback((event: ReactPointerEvent) => {
    dragRef.current = null;
    stageRef.current?.releasePointerCapture?.(event.pointerId);
  }, []);

  const imageAspectRef = useRef(1);
  const handleImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        imageAspectRef.current = img.naturalWidth / img.naturalHeight;
      }
    },
    [],
  );

  const displayedAspect = isQuarterTurn ? 1 / imageAspectRef.current : imageAspectRef.current;

  const selectCropPreset = useCallback(
    (aspect: number | null) => {
      setCropAspect(aspect);
      if (aspect === null) {
        // Free: keep the current rect, or start with the full frame.
        setCrop((prev) => prev ?? { x: 0, y: 0, width: 1, height: 1 });
      } else {
        setCrop(centredCropForAspect(aspect, displayedAspect));
      }
    },
    [displayedAspect],
  );

  const clearCrop = useCallback(() => {
    setCrop(null);
    setCropAspect(null);
  }, []);

  // ─── Rotation / flips ──────────────────────────────────────────────────────

  const rotateClockwise = useCallback(() => {
    setRotate((r) => ((r + 90) % 360) as Rotation);
    // Crop coordinates are relative to the rotated frame; a rotation
    // invalidates them, so reset rather than silently mis-crop.
    setCrop(null);
    setCropAspect(null);
  }, []);

  // ─── Save ──────────────────────────────────────────────────────────────────

  const recipe = useMemo(
    () => buildRecipe(rotate, flipH, flipV, crop, border, adjust),
    [rotate, flipH, flipV, crop, border, adjust],
  );
  const noop = isNoopRecipe(recipe);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    const validationError = validateRecipe(recipe);
    if (validationError) {
      setSaveError(validationError);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/v1/edits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          order_id: orderId,
          source_scan_id: scanId,
          recipe,
        }),
      });
      if (!res.ok) {
        let message = 'Could not save the edit. Please try again.';
        try {
          const body = await res.json();
          if (res.status < 500 && typeof body?.error === 'string') {
            message = body.error;
          }
        } catch {
          // keep the generic message
        }
        setSaveError(message);
        return;
      }
      // The edit lands in the "My edits" roll as a processing tile.
      await queryClient.invalidateQueries({ queryKey: ['scans', orderId] });
      navigate(galleryPath);
    } catch {
      setSaveError('Could not save the edit. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [recipe, orderId, scanId, queryClient, navigate, galleryPath]);

  // ─── Render ────────────────────────────────────────────────────────────────

  if (configLoading || isLoading) {
    return (
      <main className={styles.page}>
        <p className={styles.loading}>Loading editor...</p>
      </main>
    );
  }

  if (isError || !scan) {
    return (
      <main className={styles.page}>
        <Link to={galleryPath} className={styles.backLink}>← Back to gallery</Link>
        <p className={styles.error} role="alert">
          {isError ? 'Something went wrong loading this photo.' : 'Photo not found.'}
        </p>
      </main>
    );
  }

  // Edits always start from a lab master.
  if (scan.source_scan_id) {
    return (
      <main className={styles.page}>
        <Link to={galleryPath} className={styles.backLink}>← Back to gallery</Link>
        <p className={styles.error} role="alert">
          Edited photos can't be edited again. Start from the original scan instead.
        </p>
      </main>
    );
  }

  const cropStyles = crop
    ? {
        left: `${crop.x * 100}%`,
        top: `${crop.y * 100}%`,
        width: `${crop.width * 100}%`,
        height: `${crop.height * 100}%`,
      }
    : undefined;

  return (
    <main className={styles.page}>
      <div className={styles.topBar}>
        <Link to={galleryPath} className={styles.backLink}>← Back to gallery</Link>
        <h1 className={styles.heading}>Edit {scan.original_filename}</h1>
      </div>

      <div className={styles.layout}>
        {/* Stage */}
        <div className={styles.stageColumn}>
          <div
            className={`${styles.stage} ${border ? styles.stageWithBorder : ''}`}
            style={border ? { backgroundColor: border.color, aspectRatio: `${border.aspect_w} / ${border.aspect_h}` } : undefined}
          >
            <div
              ref={stageRef}
              className={styles.imageBox}
              style={{
                aspectRatio: `${displayedAspect}`,
                ...(border ? { margin: `${border.margin * 100}%` } : {}),
              }}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <img
                src={scan.thumbnail_url}
                alt={scan.original_filename}
                className={styles.previewImage}
                style={{ transform, filter: cssFilter }}
                onLoad={handleImageLoad}
                draggable={false}
              />

              {crop && (
                <div
                  className={styles.cropRect}
                  style={cropStyles}
                  role="group"
                  aria-label="Crop area"
                  onPointerDown={(e) => startDrag(e, 'move')}
                >
                  {CROP_HANDLES.map((handle) => (
                    <span
                      key={handle}
                      className={`${styles.cropHandle} ${HANDLE_CLASS[handle]}`}
                      aria-label={`Resize crop (${handle})`}
                      onPointerDown={(e) => startDrag(e, 'resize', handle)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className={styles.stageHint}>
            The preview uses a web-quality image; your edit is applied to the
            full-resolution scan when you save.
          </p>
        </div>

        {/* Controls */}
        <div className={styles.controls}>
          <section className={styles.controlGroup}>
            <h2 className={styles.controlHeading}>Rotate & flip</h2>
            <div className={styles.buttonRow}>
              <button type="button" className={styles.toolButton} onClick={rotateClockwise}>
                Rotate 90°
              </button>
              <button
                type="button"
                className={`${styles.toolButton} ${flipH ? styles.toolButtonActive : ''}`}
                aria-pressed={flipH}
                onClick={() => setFlipH((v) => !v)}
              >
                Flip horizontal
              </button>
              <button
                type="button"
                className={`${styles.toolButton} ${flipV ? styles.toolButtonActive : ''}`}
                aria-pressed={flipV}
                onClick={() => setFlipV((v) => !v)}
              >
                Flip vertical
              </button>
            </div>
          </section>

          <section className={styles.controlGroup}>
            <h2 className={styles.controlHeading}>Crop</h2>
            <div className={styles.buttonRow}>
              {CROP_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className={`${styles.toolButton} ${
                    crop && cropAspect === preset.aspect ? styles.toolButtonActive : ''
                  }`}
                  aria-pressed={!!crop && cropAspect === preset.aspect}
                  aria-label={`Crop ${preset.label}`}
                  onClick={() => selectCropPreset(preset.aspect)}
                >
                  {preset.label}
                </button>
              ))}
              {crop && (
                <button type="button" className={styles.resetButton} onClick={clearCrop}>
                  Remove crop
                </button>
              )}
            </div>
          </section>

          <section className={styles.controlGroup}>
            <h2 className={styles.controlHeading}>Border</h2>
            <div className={styles.buttonRow}>
              {BORDER_PRESETS.map((preset) => {
                const active =
                  border?.aspect_w === preset.aspect_w && border?.aspect_h === preset.aspect_h;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    className={`${styles.toolButton} ${active ? styles.toolButtonActive : ''}`}
                    aria-pressed={active}
                    aria-label={`Border ${preset.label}`}
                    onClick={() =>
                      setBorder((prev) => ({
                        aspect_w: preset.aspect_w,
                        aspect_h: preset.aspect_h,
                        color: prev?.color ?? '#ffffff',
                        margin: prev?.margin ?? 0.05,
                      }))
                    }
                  >
                    {preset.label}
                  </button>
                );
              })}
              {border && (
                <button type="button" className={styles.resetButton} onClick={() => setBorder(null)}>
                  Remove border
                </button>
              )}
            </div>
            {border && (
              <>
                <div className={styles.buttonRow}>
                  <button
                    type="button"
                    className={`${styles.toolButton} ${border.color === '#ffffff' ? styles.toolButtonActive : ''}`}
                    aria-pressed={border.color === '#ffffff'}
                    onClick={() => setBorder({ ...border, color: '#ffffff' })}
                  >
                    White
                  </button>
                  <button
                    type="button"
                    className={`${styles.toolButton} ${border.color === '#000000' ? styles.toolButtonActive : ''}`}
                    aria-pressed={border.color === '#000000'}
                    onClick={() => setBorder({ ...border, color: '#000000' })}
                  >
                    Black
                  </button>
                </div>
                <label className={styles.slider}>
                  <span className={styles.sliderLabel}>
                    Margin <span className={styles.sliderValue}>{Math.round(border.margin * 100)}%</span>
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={0.25}
                    step={0.01}
                    value={border.margin}
                    onChange={(e) => setBorder({ ...border, margin: Number(e.target.value) })}
                  />
                </label>
              </>
            )}
          </section>

          <section className={styles.controlGroup}>
            <h2 className={styles.controlHeading}>Adjust</h2>
            {SLIDERS.map((slider) => {
              const value = adjust[slider.key];
              const identity = IDENTITY_ADJUST[slider.key];
              return (
                <label key={slider.key} className={styles.slider}>
                  <span className={styles.sliderLabel}>
                    {slider.label}{' '}
                    <span className={styles.sliderValue}>
                      {slider.key === 'hue' ? `${value}°` : value.toFixed(2)}
                    </span>
                    {value !== identity && (
                      <button
                        type="button"
                        className={styles.miniReset}
                        aria-label={`Reset ${slider.label}`}
                        onClick={() => setAdjust((a) => ({ ...a, [slider.key]: identity }))}
                      >
                        Reset
                      </button>
                    )}
                  </span>
                  <input
                    type="range"
                    min={slider.min}
                    max={slider.max}
                    step={slider.step}
                    value={value}
                    aria-label={slider.label}
                    onChange={(e) =>
                      setAdjust((a) => ({ ...a, [slider.key]: Number(e.target.value) }))
                    }
                  />
                </label>
              );
            })}
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => setAdjust(IDENTITY_ADJUST)}
            >
              Reset all adjustments
            </button>
          </section>

          {saveError && (
            <p className={styles.error} role="alert">
              {saveError}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => navigate(galleryPath)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={saving || noop}
              title={noop ? 'Make at least one change before saving' : undefined}
            >
              {saving ? 'Saving…' : 'Save to My edits'}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
